import React, { useState, useEffect, useCallback } from 'react';
import { Puck, Config } from '@measured/puck';
// Import CSS - this is a fallback. Primary CSS is loaded in public/index.html
import '@measured/puck/dist/index.css';
// Import custom CSS that fixes drag and drop issues and styling
import './custom-puck.css';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { Save as SaveIcon, Publish as PublishIcon } from '@mui/icons-material';

// Import custom components
import Hero from './components/Hero';
import TextBlock from './components/TextBlock';
import ImageBlock from './components/ImageBlock';
import VideoBlock from './components/VideoBlock';
import FeatureList from './components/FeatureList';
import ContactForm from './components/ContactForm';

// Create a fallback component for error handling
const FallbackComponent = ({ componentName }) => (
  <Box 
    sx={{ 
      p: 4, 
      border: '2px dashed #ccc', 
      borderRadius: 2, 
      textAlign: 'center',
      bgcolor: '#f9f9f9',
      my: 2
    }}
  >
    <Typography color="error" variant="h6">
      Component Error: {componentName}
    </Typography>
    <Typography variant="body2">
      This component failed to render. Please check your configuration.
    </Typography>
  </Box>
);

// Log that components are being imported
console.log('PuckEditor: Importing components');
console.log('Hero component:', Hero ? 'Loaded' : 'Not loaded');
console.log('TextBlock component:', TextBlock ? 'Loaded' : 'Not loaded');
console.log('ImageBlock component:', ImageBlock ? 'Loaded' : 'Not loaded');
console.log('VideoBlock component:', VideoBlock ? 'Loaded' : 'Not loaded');
console.log('FeatureList component:', FeatureList ? 'Loaded' : 'Not loaded');
console.log('ContactForm component:', ContactForm ? 'Loaded' : 'Not loaded');

// Create an error boundary component
class PuckErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('PuckEditor error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, border: '1px solid #f44336', borderRadius: 2, bgcolor: '#ffebee' }}>
          <Typography color="error" variant="h6">Something went wrong with the page editor</Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {this.state.error?.message || 'An unknown error occurred'}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Reload Editor
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

const PuckEditor = ({ 
  data, 
  onChange, 
  readOnly = false, 
  accountId, 
  microSiteId, 
  pageId,
  onSave,
  onPublish,
  isSaving = false,
  isPublishing = false
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [sanitizedData, setSanitizedData] = useState(null);

  // Define render mode - 'dom' for production (more stable), 'iframe' for development (more isolated)
  const puckRenderMode = process.env.NODE_ENV === 'production' ? 'dom' : 'iframe';

  // Safe drag and drop handlers with error prevention
  const safeDragStart = useCallback((event) => {
    try {
      console.log('Drag started');
      // We intentionally don't do anything else to avoid errors
    } catch (error) {
      console.warn('Error in drag start handler (prevented):', error);
    }
  }, []);

  const safeDragEnd = useCallback((event) => {
    try {
      console.log('Drag ended');
      // We intentionally don't do anything else to avoid errors
    } catch (error) {
      console.warn('Error in drag end handler (prevented):', error);
    }
  }, []);

  // Ensure essential Puck styles are applied - this addresses CSS loading issues on some platforms
  useEffect(() => {
    const ensurePuckStyles = () => {
      // Check if the Puck editor is rendered
      const editor = document.querySelector('.puck-editor');
      if (!editor) return;
      
      // Check if styles are applied correctly
      const computedStyle = window.getComputedStyle(editor);
      const hasStyles = computedStyle.display === 'flex';
      
      if (!hasStyles) {
        console.log('Applying essential Puck styles inline');
        
        // Apply essential styles directly to elements
        editor.style.display = 'flex';
        editor.style.height = '100%';
        editor.style.overflow = 'hidden';
        
        // Find and style the inner editor
        const inner = document.querySelector('.puck-editor-inner');
        if (inner) {
          inner.style.display = 'flex';
          inner.style.flexDirection = 'column';
          inner.style.width = '100%';
          inner.style.height = '100%';
        }
        
        // Find and style drop zones
        const dropZones = document.querySelectorAll('.puck-drop-zone');
        dropZones.forEach(zone => {
          zone.style.minHeight = '100px';
          zone.style.position = 'relative';
        });
      }
    };
    
    // Run once on mount
    ensurePuckStyles();
    
    // Also run after a delay to catch elements that render later
    const timeoutId = setTimeout(ensurePuckStyles, 500);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Apply critical styles programmatically as a last resort
  useEffect(() => {
    const applyEssentialStyles = () => {
      const editor = document.querySelector('.puck-editor');
      if (!editor) return;
      
      // Only apply if styles aren't already applied
      const computedStyle = window.getComputedStyle(editor);
      const hasStyles = computedStyle.display === 'flex';
      
      if (!hasStyles) {
        console.log('Applying essential Puck styles programmatically');
        
        // Apply to editor
        editor.style.display = 'flex';
        editor.style.height = '100%';
        editor.style.overflow = 'hidden';
        editor.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        
        // Apply to inner container
        const inner = document.querySelector('.puck-editor-inner');
        if (inner) {
          inner.style.display = 'flex';
          inner.style.flexDirection = 'column';
          inner.style.width = '100%';
          inner.style.height = '100%';
        }
        
        // Apply to drop zones
        const dropZones = document.querySelectorAll('.puck-drop-zone');
        dropZones.forEach(zone => {
          zone.style.minHeight = '100px';
          zone.style.position = 'relative';
        });
        
        // Apply to draggable elements
        const draggables = document.querySelectorAll('[draggable]');
        draggables.forEach(el => {
          el.style.userSelect = 'none';
        });
        
        // Apply to placeholders
        const placeholders = document.querySelectorAll('[data-puck-drag-placeholder]');
        placeholders.forEach(el => {
          el.style.backgroundColor = '#f5f5f5';
          el.style.border = '2px dashed #5c2d91';
          el.style.minHeight = '100px';
          el.style.display = 'flex';
          el.style.alignItems = 'center';
          el.style.justifyContent = 'center';
          el.style.pointerEvents = 'none';
        });
      }
    };
    
    // Apply styles immediately and after a delay (in case elements render later)
    applyEssentialStyles();
    const timeout = setTimeout(applyEssentialStyles, 500);
    
    return () => clearTimeout(timeout);
  }, []);

  // Function to deeply sanitize data to prevent undefined errors
  const sanitizeData = (inputData) => {
    if (!inputData) {
      return {
        content: [],
        root: {
          props: {
            style: {
              backgroundColor: '#ffffff'
            }
          }
        }
      };
    }

    // Create a deep copy to avoid mutating the original
    const result = JSON.parse(JSON.stringify(inputData));
    
    // Ensure root exists and has the right structure
    if (!result.root) {
      result.root = {
        props: {
          style: {
            backgroundColor: '#ffffff'
          }
        }
      };
    } else if (!result.root.props) {
      result.root.props = {
        style: {
          backgroundColor: '#ffffff'
        }
      };
    } else if (!result.root.props.style) {
      result.root.props.style = {
        backgroundColor: '#ffffff'
      };
    }
    
    // Ensure content is an array
    if (!Array.isArray(result.content)) {
      result.content = [];
    } else {
      // Sanitize each content item
      result.content = result.content.map(item => {
        // Ensure each item has a valid type
        if (!item || !item.type || typeof item.type !== 'string') {
          console.warn('Invalid content item found, removing:', item);
          return null;
        }
        
        // Ensure props exists
        if (!item.props) {
          item.props = {};
        }
        
        return item;
      }).filter(Boolean); // Remove null items
    }
    
    return result;
  };

  useEffect(() => {
    console.log('PuckEditor received data:', data);
    
    try {
      // Sanitize the data to prevent errors
      const cleanData = sanitizeData(data);
      console.log('Sanitized data:', cleanData);
      
      // Update state with sanitized data
      setSanitizedData(cleanData);
      
      // If the data was significantly different, notify parent
      if (JSON.stringify(cleanData) !== JSON.stringify(data)) {
        console.warn('Data was sanitized due to potential issues');
        onChange(cleanData);
      }
    } catch (err) {
      console.error('Error sanitizing data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [data, onChange]);

  // Handle content changes
  const handleContentChange = (newContent) => {
    try {
      // Sanitize the new content before passing it up
      const cleanContent = sanitizeData(newContent);
      setHasChanges(true);
      setSanitizedData(cleanContent);
      onChange(cleanContent);
    } catch (err) {
      console.error('Error handling content change:', err);
      setError(err);
    }
  };

  // Apply a global toString error interceptor for drag and drop operations
  useEffect(() => {
    // Function to patch toString methods to prevent errors
    const patchToStringMethods = () => {
      // Store original toString method
      const originalObjectToString = Object.prototype.toString;
      
      // Override toString to handle undefined/null cases
      Object.prototype.toString = function() {
        if (this === undefined || this === null) {
          return '[object Undefined]';
        }
        return originalObjectToString.call(this);
      };
      
      // Find and patch all puck-related elements
      setTimeout(() => {
        const puckElements = document.querySelectorAll('.puck-editor, .puck-root, [data-puck-id]');
        puckElements.forEach(el => {
          if (el && typeof el.toString === 'function') {
            const originalToString = el.toString;
            el.toString = function() {
              try {
                return originalToString.call(this);
              } catch (e) {
                return '[object Element]';
              }
            };
          }
        });
      }, 1000);
      
      // Return cleanup function
      return () => {
        Object.prototype.toString = originalObjectToString;
      };
    };
    
    // Only apply in production to avoid development issues
    if (process.env.NODE_ENV === 'production') {
      return patchToStringMethods();
    }
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, border: '1px solid #f44336', borderRadius: 2, bgcolor: '#ffebee' }}>
        <Typography color="error" variant="h6">Error loading editor</Typography>
        <Typography variant="body2">{error.message}</Typography>
      </Box>
    );
  }

  // Update component fields to use standard field types
  const updateComponentFields = (fields) => {
    if (!fields) return {};
    
    const updatedFields = {};
    
    Object.keys(fields).forEach(key => {
      const field = fields[key];
      
      // Convert field types to standard Puck field types
      if (field.type === 'text' || field.type === 'color') {
        updatedFields[key] = {
          ...field,
          type: 'text' // Use standard text field for both text and color
        };
      } else if (field.type === 'rich-text') {
        updatedFields[key] = {
          ...field,
          type: 'textarea' // Use textarea for rich-text
        };
      } else if (field.type === 'select') {
        updatedFields[key] = {
          ...field,
          type: 'select' // Keep select as is
        };
      } else {
        // For any other field types, keep as is
        updatedFields[key] = field;
      }
    });
    
    return updatedFields;
  };

  // Create a wrapper function to make any component render robust against errors
  const createRobustRender = (Component, componentName) => {
    return (props) => {
      try {
        // Sanitize props to avoid toString errors
        const safeProps = {};
        Object.keys(props).forEach(key => {
          if (props[key] !== undefined && props[key] !== null) {
            safeProps[key] = props[key];
          }
        });
        
        return <Component {...safeProps} />;
      } catch (err) {
        console.error(`Error rendering ${componentName}:`, err);
        return <FallbackComponent componentName={componentName} />;
      }
    };
  };

  // Safely create the config object with error handling for each component
  const config = {
    components: {
      Hero: {
        label: "Hero Section",
        render: createRobustRender(Hero, "Hero"),
        defaultProps: Hero.defaultProps || {},
        fields: updateComponentFields(Hero.fields || {}),
        // Add componentOptions if available
        ...(Hero.componentOptions ? { componentOptions: Hero.componentOptions } : {})
      },
      TextBlock: {
        label: "Text Block",
        render: createRobustRender(TextBlock, "TextBlock"),
        defaultProps: TextBlock.defaultProps || {},
        fields: updateComponentFields(TextBlock.fields || {})
      },
      ImageBlock: {
        label: "Image Block",
        render: createRobustRender(ImageBlock, "ImageBlock"),
        defaultProps: ImageBlock.defaultProps || {},
        fields: updateComponentFields(ImageBlock.fields || {})
      },
      VideoBlock: {
        label: "Video Block",
        render: createRobustRender(VideoBlock, "VideoBlock"),
        defaultProps: VideoBlock.defaultProps || {},
        fields: updateComponentFields(VideoBlock.fields || {})
      },
      FeatureList: {
        label: "Feature List",
        render: createRobustRender(FeatureList, "FeatureList"),
        defaultProps: FeatureList.defaultProps || {},
        fields: updateComponentFields(FeatureList.fields || {})
      },
      ContactForm: {
        label: "Contact Form",
        render: (props) => {
          try {
            // Special case for ContactForm which needs extra props
            return <ContactForm {...props} accountId={accountId} microSiteId={microSiteId} pageId={pageId} />;
          } catch (err) {
            console.error('Error rendering ContactForm:', err);
            return <FallbackComponent componentName="ContactForm" />;
          }
        },
        defaultProps: ContactForm.defaultProps || {},
        fields: updateComponentFields(ContactForm.fields || {})
      }
    }
  };

  // Define viewports for responsive design
  const viewports = [
    {
      width: 1440,
      height: "auto",
      label: "Desktop",
    },
    {
      width: 768,
      height: "auto",
      label: "Tablet",
    },
    {
      width: 375,
      height: "auto",
      label: "Mobile",
    }
  ];

  return (
    <PuckErrorBoundary>
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
        <Puck
          config={config}
          data={sanitizedData}
          onPublish={handleContentChange}
          onChange={handleContentChange}
          readOnly={readOnly}
          viewports={viewports}
          renderMode={puckRenderMode}
          onDragStart={safeDragStart}
          onDragEnd={safeDragEnd}
        />
        
        {/* Floating action bar */}
        {!readOnly && (
          <Box 
            sx={{ 
              position: 'fixed', 
              bottom: 20, 
              right: 20, 
              zIndex: 1000,
              display: 'flex',
              gap: 1
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={onSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            
            {onPublish && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PublishIcon />}
                onClick={onPublish}
                disabled={isPublishing || !hasChanges}
              >
                {isPublishing ? 'Publishing...' : 'Publish'}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </PuckErrorBoundary>
  );
};

export default PuckEditor; 